'use strict';

export default angular
  .module('maestro.services.data.subscriptions', [
    'maestro.services.utils',
    'maestro.services.webcore'
  ])
  .service('SubscriptionsService', SubscriptionsService)
  .name;

function SubscriptionsService(webcore, $q, CacheService) {

  /**
   * Get all available in the current organization subscriptions (service plans).
   * Cached function. Use this function in views.
   * @param {number} organizationId organization id
   * @param {string} [appName] app name. Most probably you find it in `organization.brand` field
   * @return {Promise}
   */
  this.getOrganizationSubscriptions = CacheService.cachePromiseFunc((organizationId, appName) => {
    let me = this;
    if (!organizationId) {
      return $q.reject();
    }
    return me.getAllServicePlans(void 0, void 0, organizationId, void 0, true)
      .then(freePlansData => {
        if (appName) {
          return me.getAllServicePlans(void 0, void 0, void 0, appName, true)
            .then(paidPlansData => {
              // Combine both type of plans
              let freePlans = freePlansData.servicePlans || [];
              let paidPlans = (paidPlansData.servicePlans || [])
                .filter(paidPlan => {
                  if (freePlans.find(i => i.id === paidPlan.id)) { // except free plans
                    return false;
                  }
                  // Set appName for paid plans
                  paidPlan.appName = appName;
                  return true;
                });
              let allPlans = freePlans.concat(paidPlans);
              return allPlans;
            });
        }
        let allPlans = freePlansData.servicePlans || [];
        return allPlans;
      });
  });

  /**
   * Get available service plans.
   * @param {boolean} plansOnly Means we want to fetch plans only information, without checking for availablility for subscription, etc.
   */
  this.getAvailableServicePlans = (locationId, userId, organizationId, appName, plansOnly) => {
    return $q.when(
      //'false' for hiddenPrices
      webcore.services.subscriptions.getAvailableServicePlans(locationId, userId, organizationId, appName, false, plansOnly)
        .then(data => {
            data.servicePlans = (data.servicePlans && angular.isArray(data.servicePlans))
              ? data.servicePlans.filter(sp => plansOnly || sp.available)
              : [];
            return $q.resolve(data);
          },
          err => $q.reject(err))
    );
  };

  this.getAllServicePlans = (locationId, userId, organizationId, appName, plansOnly) => {
    return $q.when(
      //'false' for hiddenPrices
      webcore.services.subscriptions.getAvailableServicePlans(locationId, userId, organizationId, appName, false, plansOnly)
        .then(data => {
            data.servicePlans = data.servicePlans || [];
            return $q.resolve(data);
          },
          err => $q.reject(err))
    );
  };

  /**
   * Get location subscriptions (service plans for location)
   */
  this.getLocationSubscriptions = (locationId, userPlanId, status, getCard) => {
    return $q.when(
      webcore.services.subscriptions.getLocationSubscriptions(locationId, null, userPlanId, status, getCard)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get user subscriptions (service plans user subscribed to).
   */
  this.getUserSubscriptions = (userId, status, getCard) => {
    return $q.when(
      webcore.services.subscriptions.getUserSubscriptions(userId, status, getCard)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Grants specified service plan (sybscription) to specified location(s).
   * @param servicePlanId Id of the service plan to grant
   * @param locations Array of objects with user Id and location Id to grant subscription to
   * @param organizationId Organization in terms of which the service plans are granted to users
   * @param endDate Date when the service plan subscription should expire
   */
  this.grantSubscriptionToLocations = (servicePlanId, locations, organizationId, endDate) => {
    let services = {
      locations: locations
    };
    return $q.when(
      webcore.services.subscriptions.grantSubscriptionToLocations(servicePlanId, services, organizationId, endDate)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Provide new purchase info when appying new subscription (paid plan)
   * @param price Mandatory
   * @param locationId Mandatory as subscription linked to location
   * @param userId Mandatory purchase from the name of specific user
   * @param subscriptionId Mandatory use existing Braintree subscription ID
   * @param [sandbox] Braintree instance, boolean
   * @param [site] Merchant identifier as site
   * @returns {*}
   */
  this.purchaseSubscription = (price, locationId, userId, subscriptionId, sandbox, site) => {
    let purchaseUserId = userId || void 0;
    let purchaseInfo = {
      subscriptionId: subscriptionId,
      site: price.site || site || void 0
    };

    // Do not provide price for Shopify Subscriptions
    let givenPrice = (!price || price.paymentType === 5) ? void 0 : price.id;

    return $q.when(
      webcore.services.subscriptions.provideNewPurchaseInfo(
        purchaseInfo,
        locationId,
        price.paymentType,
        purchaseUserId,
        givenPrice,
        sandbox)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Update purchase info for current subscription (paid plan)
   * @param userPlanId Mandatory, current user plan ID to update
   * @param locationId Mandatory as subscription linked to location
   * @param userId Mandatory purchase from the name of specific user
   * @param subscriptionId Mandatory use existing Braintree subscription ID
   * @returns {*}
   */
  this.updatePurchaseInfo = (userPlanId, locationId, userId, subscriptionId) => {
    return $q.when(
      webcore.services.subscriptions.updatePurchaseInfo(
        { 'subscriptionId': subscriptionId },
        userPlanId,
        locationId,
        userId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Upgrades the specified user plan (subscription) to the target plan (subscription).
   * @param userPlanId
   * @param targetPlanId
   * @param userId
   * @returns {*}
   */
  this.upgradeUserSubscription = (userPlanId, targetPlanId, userId) => {
    return $q.when(
      webcore.services.subscriptions.upgradeSubscription(
        userPlanId,
        targetPlanId,
        userId,
        {})
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Cancels specified plan (subscription).
   * @param userPlanId
   * @param locationId
   * @param organizationId
   * @returns {*}
   */
  this.cancelSubscription = (userPlanId, locationId, organizationId) => {
    return $q.when(
      webcore.services.subscriptions.cancelSubscription(userPlanId, locationId, organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets payment transactions history for the specified subscription (service plan).
   * @param userPlanId
   * @param locationId
   * @returns {*}
   */
  this.getSubscriptionTransactions = (userPlanId, locationId) => {
    return $q.when(
      webcore.services.subscriptions.getSubscriptionTransactions(userPlanId, locationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Gets history of the subscription upgrade/downgrade events
   * @param userPlanId
   * @param locationId
   * @returns {*}
   */
  this.getSubscriptionHistory = (userPlanId, locationId) => {
    return $q.when(
      webcore.services.subscriptions.getSubscriptionUpgradeDowngradeHistory(userPlanId, locationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

}
